import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "@/common/theme/rootStyle.css";
import { app as microsoftApp } from "@microsoft/teams-js";
import {
	mapTeamsTheme,
	mapAzureCommunicationTheme,
} from "@/common/theme/teamsThemeMapper";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import {
	ServerEvent,
	NotificationContext,
	NotificationManager,
} from "@/common/notification";
import { IntlContext, IntlProvider, IntlShape } from "react-intl";
import { getLanguageMap } from "@/common/language";
import { Router } from "@/common/router";
import { AuthenticationProvider } from "@/common/authentication";
import { StorageKeys } from "@/common/constants/storageKeys";
import { BlockingDialog } from "@/components/atoms/blockingDialog";
import { AuthorizationProvider } from "@/common/authorization";
import { GlobalDataProvider } from "./common/globalData";
import MessageProvider from "./common/messages/context";
import GlobalDialogProvider from "./components/atoms/dialog/context";
import { FallbackComponent } from "./components/organisms/fallback/FallbackComponent";
import {
	DEFAULT_COMPONENT_ICONS,
	FluentThemeProvider,
	lightTheme,
} from "@azure/communication-react";
import { initializeIcons, registerIcons } from "@fluentui/react";
import {
	FluentProvider,
	mergeClasses,
	teamsLightTheme,
} from "@fluentui/react-components";
import { initializeFileTypeIcons } from "@fluentui/react-file-type-icons";
import { ArrowDownloadRegular } from "@fluentui/react-icons";
import { MsalProvider } from "@azure/msal-react";
import { embededInTeams } from "./common/authentication/authenticationUtils";
import { MsalInstance } from "./common/authentication/msalConfig";
import { AcsProvider } from "./common/acs/AcsProvider";
import { WebchatVideoCallDialer } from "./common/acs/WebchatVideoCallDialer";
import {
	NavigationState,
	useNavigationStore,
} from "./common/stores/persistingStores/navigation";
import { useResponsiveBreakpoints } from "./hooks/responsive";
import { forceReloadForRelease } from "./common/helpers/serviceWorkerManager";
import {
	DebugInfoKey,
	WindowReloadReason,
	useDebugStore,
} from "./common/stores/persistingStores/debugStore";
import { useTeamsContextStore } from "./common/stores/nonpersistingStores/teamsContext";

export interface AppProps {
	dependencies: {
		notificationManager: NotificationManager;
		queryClient: QueryClient;
	};
}

registerIcons({
	icons: {
		downloadattachment: <ArrowDownloadRegular />,
		...DEFAULT_COMPONENT_ICONS,
	},
});
initializeIcons();
initializeFileTypeIcons();

const App = (props: AppProps) => {
	const { dependencies } = props;
	const [theme, setTheme] = useState<any>(teamsLightTheme);
	const [chatTheme, setChatTheme] = useState<any>(lightTheme);
	const [language, setLanguage] = useState<string>(navigator.language);
	const messages = useMemo(() => getLanguageMap(language), [language]);
	const [hasMultipleClients, setMultipleClients] = useState<boolean>(false);
	const [isSignalRDisconnected, setIsSignalRDisconnected] =
		useState<boolean>(false);
	const isSignalRDisconnectedRef = useRef<boolean>(false);
	const receivedHeartbeatTime = useRef<Date | null>(null);
	const heartbeatExpectationTimeout = useRef<NodeJS.Timeout | null>(null);
	const notificationManager = dependencies.notificationManager;
	const pushDebugInfoItem = useDebugStore(
		(state) => state.pushDebugInfoItem
	);

	const setSignalRDisconnected = useCallback((disconnected: boolean) => {
		if (isSignalRDisconnectedRef.current === disconnected) {
			return;
		}
		isSignalRDisconnectedRef.current = disconnected;
		setIsSignalRDisconnected(disconnected);
	}, []);

	const toggledTab = useNavigationStore(
		(state: NavigationState) => state.toggledTab
	);

	const { isMobile } = useResponsiveBreakpoints();

	const checkMultipleClients = useCallback((browserSessionId: string) => {
		const currentBrowserSessionId = sessionStorage.getItem(
			StorageKeys.BrowserSessionId
		) as string;

		if (
			currentBrowserSessionId &&
			currentBrowserSessionId !== browserSessionId
		) {
			setMultipleClients(true);
		}

		return () => {
			notificationManager.disconnect();
		};
	}, []);

	const processSignalRHeartbeat = useCallback(
		(serverTimestampIsoString: string) => {
			const timeoutThreshold = 1000 * 60 * 3; // 3 minutes
			const serverTimestampDate = new Date(serverTimestampIsoString);
			if (receivedHeartbeatTime.current !== null) {
				const timeDifference =
					serverTimestampDate.getTime() -
					receivedHeartbeatTime.current.getTime();
				if (timeDifference > timeoutThreshold) {
					// 3 minutes
					setSignalRDisconnected(true);
				} else {
					setSignalRDisconnected(false);
				}
			} else {
				setSignalRDisconnected(false);
			}

			if (heartbeatExpectationTimeout.current) {
				clearTimeout(heartbeatExpectationTimeout.current);
			}

			heartbeatExpectationTimeout.current = setTimeout(() => {
				setSignalRDisconnected(true);
				pushDebugInfoItem(
					DebugInfoKey.WindowReloadTriggerEvents,
					WindowReloadReason.HeartbeatTimeout
				);
				// Transport may be silently dead while still reporting Connected
				// (server-side event source died, sticky-routed half-open socket).
				// Force a stop() so the constructor's onclose handler fires, which
				// runs forceReloadForRelease via the registered onClose callback.
				notificationManager.disconnect();
			}, timeoutThreshold); // heartbeat should be sent each 2 minutes, but will allow for one extra minute to receive it

			receivedHeartbeatTime.current = serverTimestampDate;
		},
		[setSignalRDisconnected, pushDebugInfoItem, notificationManager]
	);

	useEffect(() => {
		if (embededInTeams) {
			microsoftApp.registerOnThemeChangeHandler((theme: string) => {
				setTheme(mapTeamsTheme(theme));
				setChatTheme(mapAzureCommunicationTheme(theme));
				localStorage.setItem(StorageKeys.TeamsTheme, theme);
			});

			microsoftApp.getContext().then((context: microsoftApp.Context) => {
				setTheme(mapTeamsTheme(context.app.theme));
				setChatTheme(mapAzureCommunicationTheme(context.app.theme));
				setLanguage(context.app.locale);
				localStorage.setItem(StorageKeys.TeamsTheme, context.app.theme);
				useTeamsContextStore
					.getState()
					.setHostClientType(context.app.host.clientType);
			});
		}

		notificationManager.subscribe(
			ServerEvent.SignedOtherBrowser,
			checkMultipleClients
		);

		//#warning-ts [security] when the user signs out, it is not disconnected from signalR; it is still connected and authenticated with the last identity
		notificationManager.subscribe(
			ServerEvent.Heartbeat,
			processSignalRHeartbeat
		);

		// Intentionally not wiring onReconnecting/onReconnected: SignalR's
		// auto-reconnect (~9s window) usually recovers brief blips silently.
		// The dialog only appears on real outages — onclose (auto-reconnect
		// exhausted), the 90s heartbeat timeout, or OfflineDetector polling
		// failures.
		notificationManager.onClose(() => {
			setSignalRDisconnected(true);
			pushDebugInfoItem(
				DebugInfoKey.WindowReloadTriggerEvents,
				WindowReloadReason.SocketClosed
			);
			void forceReloadForRelease();
		});

		return () => {
			notificationManager.unsubscribe(
				ServerEvent.SignedOtherBrowser,
				checkMultipleClients
			);
			notificationManager.unsubscribe(
				ServerEvent.Heartbeat,
				processSignalRHeartbeat
			);
		};
	}, []);

	const minWidthClass = useMemo(() => {
		if (isMobile) {
			return "mobile-view";
		}

		return toggledTab !== undefined ? "toggled-view-open" : "";
	}, [isMobile, toggledTab]);

	return (
		<Sentry.ErrorBoundary fallback={FallbackComponent}>
			<IntlProvider locale={language} defaultLocale="en" messages={messages}>
				<QueryClientProvider client={dependencies.queryClient}>
					<FluentThemeProvider fluentTheme={chatTheme}>
						<FluentProvider
							theme={theme}
							className={mergeClasses("theme-provider", minWidthClass)}
						>
							<NotificationContext.Provider value={notificationManager}>
								<MsalProvider instance={MsalInstance}>
									<MessageProvider>
										<AuthenticationProvider>
											<AuthorizationProvider>
												<GlobalDataProvider>
													<IntlContext.Consumer>
														{(intl: IntlShape) => (
															<>
																<BlockingDialog
																	isOpen={isSignalRDisconnected}
																	text={intl.formatMessage({
																		id: "ErrorMessage.Offline",
																	})}
																/>
																<BlockingDialog
																	isOpen={hasMultipleClients}
																	text={intl.formatMessage({
																		id: "ErrorMessage.Inactive",
																	})}
																/>
															</>
														)}
													</IntlContext.Consumer>
													<GlobalDialogProvider>
														<AcsProvider>
															<WebchatVideoCallDialer />
															{!hasMultipleClients && <Router />}
														</AcsProvider>
													</GlobalDialogProvider>
												</GlobalDataProvider>
											</AuthorizationProvider>
										</AuthenticationProvider>
									</MessageProvider>
								</MsalProvider>
							</NotificationContext.Provider>
						</FluentProvider>
					</FluentThemeProvider>
				</QueryClientProvider>
			</IntlProvider>
		</Sentry.ErrorBoundary>
	);
};

export default App;
